import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { HOME_I18N } from "../i18n/home";

const BADGE_EN = { "GERÇEK PROJE": "REAL PROJECT", "ÇÖZÜM MİMARİSİ": "SOLUTION ARCHITECTURE" };

export default function CaseStudiesSection({ limit = 5, withHeading = true }) {
  const [items, setItems] = useState([]);
  const { lang } = useLang();
  const t = HOME_I18N[lang].cases;

  useEffect(() => {
    api.get("/projects").then((r) => {
      const all = (r.data || []).filter((p) => p.list_visible !== false);
      all.sort((a, b) => {
        const ao = a.order ?? 999;
        const bo = b.order ?? 999;
        if (ao !== bo) return ao - bo;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setItems(all.slice(0, limit));
    }).catch(() => {});
  }, [limit]);

  return (
    <section className="section bg-[#F8FAFC]" data-testid="case-studies-section">
      <div className="container-x">
        {withHeading && (
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div className="max-w-2xl">
              <span className="eyebrow">{t.eyebrow}</span>
              <h2 className="mt-3 h2-section">{t.title}</h2>
              <p className="mt-5 body-lg">{t.desc}</p>
            </div>
            <Link to="/projeler" data-testid="case-studies-view-all" className="btn-secondary">
              {t.viewAll} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        )}

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {items.map((it, i) => {
            const rawBadge = it.badge || (it.project_type === "GERÇEK PROJE" ? "GERÇEK PROJE" : "ÇÖZÜM MİMARİSİ");
            const badge = lang === "en" ? BADGE_EN[rawBadge] || rawBadge : rawBadge;
            const isReal = it.project_type === "GERÇEK PROJE" || rawBadge === "GERÇEK PROJE";
            return (
              <motion.article
                key={it.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                className={`group card-elevate overflow-hidden flex flex-col ${i === 0 ? "md:col-span-2 md:flex-row" : ""}`}
                data-testid={`case-card-${it.slug}`}
              >
                {it.cover_image && (
                  <div className={`relative overflow-hidden bg-[#0B1728] ${i === 0 ? "h-56 md:h-auto md:w-1/2" : "h-48"}`}>
                    <img src={it.cover_image} alt={it.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#07111F]/50 to-transparent" />
                    <span
                      className={`absolute top-4 left-4 inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] backdrop-blur ${
                        isReal
                          ? "border border-[#10B981]/40 bg-[#10B981]/15 text-[#34D399]"
                          : "border border-[#22D3EE]/40 bg-[#22D3EE]/15 text-[#22D3EE]"
                      }`}
                    >
                      {badge}
                    </span>
                  </div>
                )}
                <div className={`p-7 flex-1 flex flex-col ${i === 0 ? "md:w-1/2 md:justify-center" : ""}`}>
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#2563EB]">{it.sector}</div>
                  <h3 className="mt-1.5 font-heading text-xl sm:text-2xl font-bold text-[#07111F] leading-tight">
                    {it.title}
                  </h3>
                  {it.subtitle && (
                    <div className="mt-1.5 text-sm font-medium text-[#334155]/90 line-clamp-2">{it.subtitle}</div>
                  )}
                  <p className="mt-4 text-sm leading-relaxed text-[#334155] line-clamp-3">
                    {it.summary || it.solution || it.need}
                  </p>
                  <Link to={`/projeler/${it.slug}`} className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB] hover:gap-2.5 transition-all">
                    {t.explore} <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
